import { message } from "@p0security/ui";
import { doc, getDoc } from "firebase/firestore";
import { useCallback, useContext, useMemo, useState } from "react";
import { useParams } from "react-router";

import { updateHeadersInit, useApiFetch } from "../../hooks/useApiFetch";
import { useGuardedEffect } from "../../hooks/useGuardedEffect";
import { DB } from "../../providers/FirestoreProvider";
import type { Cancellation } from "../../utils/cancel";
import { staticError } from "../../utils/console";
import { UserAuthContext } from "./providers/UserAuthProvider.tsx";
import { getCurrentToken } from "./tokens/TokenProvider.ts";
import type { NormalizedUser, TenantData } from "./types/auth";
import type { UserRole } from "./util";
import { UserAuthz } from "./util";

type OrgFromLocalStorage = {
  lastAccess: Date;
};
export type OrgFromLocalStorageMap = Record<string, OrgFromLocalStorage>;
export const ORG_SLUGS_LOCALSTORAGE_KEY = "p0-previous-tenants";

export const useUser = () => {
  const auth = useContext(UserAuthContext);

  if (!auth) {
    throw new Error("useUser must be used within a UserAuthProvider");
  }

  return auth;
};

/** Retrieves the logged-in users P0 permissions as a set of roles
 *
 * An empty set indicates that permissions are still loading.
 */
export const useUserAuthz = (
  signOut: () => Promise<void>,
  user?: NormalizedUser,
  tenant?: TenantData
) => {
  const [userAuthz, setUserAuthz] = useState(new Set<UserRole>());
  const isSandbox = tenant?.state === "found" && tenant.isSandbox;
  const hasUser = user !== undefined;
  const userUid = user?.uid;
  const userTenantId = user?.tenantId;
  const userEmail = user?.email;

  useGuardedEffect(
    (cancellation) => async () => {
      const roles = new Set<UserRole>();
      const tenantId = userTenantId;

      if (cancellation.isCancelled) return;

      if (!hasUser) {
        return;
      }

      if (tenantId === undefined) {
        return await signOut();
      }

      if (!userEmail) {
        message.error(
          "Can not sign in: this user has previously signed in with a different identity provider.\nPlease contact support@p0.dev to enable this user."
        );
        return await signOut();
      }

      const hasRole = async (path: string) => {
        try {
          await getDoc(doc(DB, `o/${tenantId}/${path}`));
          return true;
        } catch (err: any) {
          // Want to continue on permission-denied in case user is different role
          if (err.code !== "permission-denied") throw err;
          return false;
        }
      };

      // Any person in the domain is a viewer (for now)
      // Validate that user can access tenant
      if (await hasRole("auth/valid")) roles.add("viewer");
      if (cancellation.isCancelled) return;

      for (const role of [
        "owner",
        "manager",
        "iamOwner",
        "iamViewer",
      ] as const) {
        // Firestore security roles handle the actual authz check;
        // a user is allowed either if
        // - their email is directly in the /bindings sub-collection
        // - their group is in the allowed.groups list in the document
        if (await hasRole(`roles/${role}/test/verify`))
          roles.add(role === "manager" ? "approver" : role);
        if (cancellation.isCancelled) return;
      }

      if (isSandbox) roles.add("iamViewer");

      if (roles.size === 0) {
        message.error("Unauthorized");
        return await signOut();
      }

      if (!cancellation.isCancelled) setUserAuthz(roles);
    },
    [isSandbox, signOut, hasUser, userUid, userTenantId, userEmail],
    staticError
  );
  return userAuthz;
};

/** Returns an authorized fetch context
 *
 * Use this fetch context just like a normal browser fetch context, with the following caveats:
 * - Path is relative to the backend tenant collection
 * - Any passed Authorization header will be ignored
 *
 * Example:
 * ```
 * const [error, setError] = useState<string>();
 * const authFetch = useAuthFetch(setError);
 * ...
 * const response = await authFetch("relative/path", {method: "POST", data});
 * if (response) {
 *    ...
 * }
 * ```
 *
 * @returns Fetch callback. Works like `fetch` except:
 *   - Can directly pass `json` values in RequestInit; this will automatically send the proper
 *     JSON request body and headers
 *   - Can pass `onNotOk` in RequestInit; this will register a handler to be applied when a non-ok
 *     status is returned (otherwise `onError` will be used)
 *   - Can pass a Cancellation object (to Automatically clean up fetches inside useEffect)
 *
 * @param onError Called when an error occurs. Note that 4XX / 5xx statuses are not errors. Will also
 *                be called on these statuses if onNotOk is not defined.
 * @param onFetch Called when a fetch is started or finished. Can be used to show a loading indicator.
 */
export const useAuthFetch = (
  onError?: (error: string | undefined) => void,
  onFetch?: (isFetching: boolean) => void
) => {
  const { orgSlug } = useParams();
  const apiFetch = useApiFetch(onError, onFetch);

  const authFetch = useCallback(
    async (
      path: string,
      init: RequestInit & {
        json?: any;
        onNotOk?: (response: Response) => void;
        cancellation?: Cancellation;
      }
    ) => {
      const token = await getCurrentToken();
      if (!token) {
        onError?.("Unauthorized");
        return;
      }

      const newHeaders: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };

      // Headers can be either a Headers object, an object, or an array of string pairs
      const headers = init.headers ?? {};
      updateHeadersInit(headers, newHeaders);

      const initWithAuth = {
        ...init,
        headers,
      };
      return await apiFetch(`o/${orgSlug}/${path}`, initWithAuth);
    },
    [apiFetch, orgSlug, onError]
  );

  return authFetch;
};

/** Returns true if user is one of any of the specified roles */
export const useHasRole = (...oneOf: UserRole[]) => {
  const roles = useContext(UserAuthz);
  const hasRole = useMemo(
    () => !!oneOf.find((r) => roles.has(r)),
    [oneOf, roles]
  );
  return hasRole;
};

export type AuthFetch = ReturnType<typeof useAuthFetch>;
